// @ts-nocheck
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, useMap, useMapEvent } from 'react-leaflet';
import 'mapbox';
import 'mapbox-gl-leaflet';
import 'leaflet-draw';
import L, { divIcon, DrawMap, Layer } from 'leaflet';
import { v7 as uuidv7 } from 'uuid';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import NiceModal from '@ebay/nice-modal-react';
import { useFormContext } from 'react-hook-form';
import * as turf from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import PvDetailsModal from '@/components/modals/PvDetailsModal';
import MapOverlay from '@/components/overlay/MapOverlay';
import ToolModal from '@/components/modals/ToolModal';

declare global {
  interface Window {
    type: boolean;
    handlePvPolygonClick: (id: number) => void;
    handlePvPolygonRemove: (id: number) => void;
  }
}

window.type = true;

L.drawLocal.draw.toolbar.buttons.polygon = 'DRAW POLYGON TEXT';

L.DrawToolbar.include({
  getModeHandlers: function (map: DrawMap) {
    return [
      {
        enabled: this.options.polygon,
        handler: new L.Draw.Polygon(map, { ...this.options.polygon }),
        title: L.drawLocal.draw.toolbar.buttons.polygon,
      },
    ];
  },
});

const createPopupContent = (title: string, id: string) => {
  return `
    <div id="polygon-${id}" class="relative">
      <button type="button" onclick="handlePvPolygonRemove(${id})" class="absolute -left-3 -top-2 bg-transparent border-0">
        <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.82667 5.99986L8.596 11.9999M5.404 11.9999L5.17333 5.99986M11.8187 3.85986C12.0467 3.89453 12.2733 3.93119 12.5 3.97053M11.8187 3.85986L11.1067 13.1152C11.0776 13.492 10.9074 13.844 10.63 14.1007C10.3527 14.3574 9.9886 14.5 9.61067 14.4999H4.38933C4.0114 14.5 3.64735 14.3574 3.36999 14.1007C3.09262 13.844 2.92239 13.492 2.89333 13.1152L2.18133 3.85986M11.8187 3.85986C11.0492 3.74354 10.2758 3.65526 9.5 3.59519M2.18133 3.85986C1.95333 3.89386 1.72667 3.93053 1.5 3.96986M2.18133 3.85986C2.95076 3.74354 3.72416 3.65526 4.5 3.59519M9.5 3.59519V2.98453C9.5 2.19786 8.89333 1.54186 8.10667 1.51719C7.36908 1.49362 6.63092 1.49362 5.89333 1.51719C5.10667 1.54186 4.5 2.19853 4.5 2.98453V3.59519M9.5 3.59519C7.83581 3.46658 6.16419 3.46658 4.5 3.59519" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div class="flex items-center flex-col justify-center gap-2 pt-1">
        <div class="not-italic font-semibold text-lg leading-7 text-[#333333]">${title}</div>
        <button type="button" class="btn btn-warning rounded-md hover:bg-opacity-90" onclick="handlePvPolygonClick(${id})">Weitere Informationen</button>
      </div>
    </div>
  `;
};

let mapboxLayer: any;

const DrawControl: FC<{
  mapInstanceRef: React.MutableRefObject<L.Map | undefined>;
  addPosition: (
    id: string,
    layer: L.Layer,
    json: Feature<Polygon>,
    area: string,
    feature?: Record<string, any>,
  ) => void;
  drawnItemsRef: React.MutableRefObject<L.FeatureGroup>;
  // initialValue: Record<string, any>;
}> = memo(
  ({
    mapInstanceRef,
    addPosition,
    drawnItemsRef,
    //    initialValue
  }) => {
    const map = useMap();
    const form = useFormContext();
    const clickDisable = useRef(false);
    const drawControlStore = useRef<L.Control.Draw>();

    const polygonIdHashMap = useRef({});
    useMapEvent('click', async e => {
      if (clickDisable.current) {
        return;
      }
      clickDisable.current = true;
      const { lat, lng } = e.latlng;

      // Construct Mapbox API request
      const mapboxToken = import.meta.env.VITE_ACCESS_TOKEN;
      const url = `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery/${lng},${lat}.json?layers=building&geometry=polygon&access_token=${mapboxToken}`;

      try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.features.length > 0) {
          console.log('Building Data:', data.features[0].properties);
        } else {
          console.log('No building data at this location.');
        }
      } catch (error) {
        console.error('Error fetching building data:', error);
      } finally {
        clickDisable.current = false;
      }

      const mapboxMap = mapboxLayer._glMap;
      const point = mapboxMap.project(e.latlng);

      const features = mapboxMap.queryRenderedFeatures(point, {
        layers: ['building'],
      });

      if (features.length > 0) {
        const building = features?.find((f: Feature) => f.geometry.type === 'Polygon');

        const featuresForBuild = mapboxMap.querySourceFeatures('composite', {
          sourceLayer: 'building',
        });

        const listCoord: any[] = [];

        featuresForBuild.map((fet: any) => {
          if (fet.id === building.id) {
            listCoord.push(turf.polygon(fet.geometry.coordinates));
          }
        });

        const singlePolygon = listCoord.length > 1 ? turf.union(turf.featureCollection(listCoord)) : listCoord[0];

        if (building) {
          const drawnItems = drawnItemsRef.current;

          if (singlePolygon) {
            const polygon = L.polygon(
              singlePolygon.geometry.coordinates[0].map(
                (coord: any) => [coord[1], coord[0]], // Swap [lon, lat] -> [lat, lon]
              ),
              {
                color: '#3388ff',
                fillColor: '#3388ff',
                fillOpacity: 0.2,
                opacity: 0.5,
              },
            );

            polygon.addTo(map);

            polygon.bindPopup(
              // @ts-ignore
              createPopupContent(`PV Fläche ${(form.getValues()?.pv_area?.length || 0) + 1}`, polygon._leaflet_id),
            );

            polygonIdHashMap.current[polygon._leaflet_id] = form.getValues('pv_area').length;
            const geoJson = polygon.toGeoJSON();

            geoJson.properties.height = building.properties.height;

            // @ts-ignore
            addPosition(polygon._leaflet_id, polygon, geoJson as Feature<Polygon>, String(turf.area(geoJson)));

            polygon.openPopup();

            drawnItems.addLayer(polygon);
          }
        } else {
          console.log('No polygon data found, only points.');
        }
      } else {
        console.log('No buildings found at this location.');
      }
      clickDisable.current = false;
    });

    // @ts-ignore
    useMapEvent('draw:drawstart', event => {
      clickDisable.current = true;
    });

    // @ts-ignore
    useMapEvent('draw:drawstop', event => {
      clickDisable.current = false;
    });

    // @ts-ignore
    useMapEvent('draw:edited', async event => {
      const layers = event.layers;
      layers.eachLayer(layer => {
        const latLngs = layer.getLatLngs();
        if (latLngs.length > 0) {
          const area = L.GeometryUtil.geodesicArea(latLngs[0]);
          form.setValue(`pv_area[${polygonIdHashMap.current[layer._leaflet_id]}].area_pv`, area);
          form.setValue(
            `pv_area[${polygonIdHashMap.current[layer._leaflet_id]}].polygon_data_pv`,
            JSON.stringify(layer.toGeoJSON().geometry.coordinates[0]),
          );
        }
      });
    });

    useEffect(() => {
      map.zoomControl.remove();
      const mapboxStreet = L.mapboxGL({
        accessToken: import.meta.env.VITE_ACCESS_TOKEN,
        style: 'mapbox://styles/thomas-kec/cmb9nl7yp00um01s67ewzdso2',
      });

      const mapboxSatellite = L.mapboxGL({
        accessToken: import.meta.env.VITE_ACCESS_TOKEN,
        style: 'mapbox://styles/thomas-kec/cmb9nl7yp00um01s67ewzdso2',
      }).addTo(map);

      mapboxLayer = mapboxSatellite;

      const baseMaps = {
        'Street View': mapboxStreet,
        'Satellite View': mapboxSatellite,
      };

      L.control
        .zoom({
          position: 'bottomright',
        })
        .addTo(map);

      L.control.layers(baseMaps).addTo(map);
    }, []);

    mapInstanceRef.current = map;

    useEffect(() => {
      const drawnItems = drawnItemsRef.current;

      map.addLayer(drawnItems);

      const drawControl = new L.Control.Draw({
        position: 'topleft',
        edit: {
          featureGroup: drawnItems,
          remove: false,
        },
        draw: {
          polyline: false,
          circle: false,
          rectangle: false,
          marker: false,
          circlemarker: false,
          polygon: {
            icon: divIcon(),
            allowIntersection: false,
            showArea: true,
            drawError: {
              color: '#e1e100',
              message: "<strong>Oh snap!<strong> you can't draw that!",
            },
          },
        },
      });

      drawControlStore.current = drawControl;

      mapInstanceRef.current.addControl(drawControl);

      map.addControl(drawControl);

      map.on(L.Draw.Event.CREATED, event => {
        const { layer } = event;

        drawnItems.addLayer(layer);

        layer.bindPopup(
          createPopupContent(`PV Fläche ${(form.getValues()?.pv_area?.length || 0) + 1}`, layer._leaflet_id),
        );

        polygonIdHashMap.current[layer._leaflet_id] = form.getValues('pv_area').length;

        const geoJson = layer.toGeoJSON();

        layer.openPopup();

        addPosition(layer._leaflet_id, layer, geoJson, String(turf.area(geoJson)));
      });

      if (Array.isArray(form.getValues('pv_area'))) {
        form.getValues('pv_area')?.forEach((feature: any, index: number) => {
          console.log('feature', feature);
          let json = null;
          try {
            json = JSON.parse(feature.polygon_data_pv);
          } catch (e) {}

          if (!json) return;

          const coordinates = JSON.parse(feature.polygon_data_pv).map(
            (coord: any) => [coord[1], coord[0]], // Swap [lon, lat] -> [lat, lon]
          );

          // Create a polygon and add it to the map
          const polygon = L.polygon(coordinates, {
            color: '#3388ff',
            fillColor: '#3388ff',
            fillOpacity: 0.2,
            opacity: 0.5,
          });

          polygon.addTo(map);

          polygon.bindPopup(
            // @ts-ignore
            createPopupContent(feature.name_pv, polygon._leaflet_id),
          );
          polygonIdHashMap.current[polygon._leaflet_id] = index;
          const geoJson = polygon.toGeoJSON();
          // @ts-ignore
          addPosition(polygon._leaflet_id, polygon, geoJson as Feature<Polygon>, String(turf.area(geoJson)), feature);

          drawnItems.addLayer(polygon);
        });
      }

      // Cleanup on unmount
      return () => {
        map.removeControl(drawControl);
      };
    }, [map]);

    return null; // No visual elements to render
  },
);

const PvAreaSelector = () => {
  const mapInstanceRef = useRef<L.Map | undefined>();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const [step, setStep] = useState<'select' | 'draw' | null>('draw');

  const [values, setValues] = useState<
    Record<
      string,
      {
        layer: L.Layer;
        id: number;
        data?: Record<string, any> | null;
        json: Feature<Polygon>;
        uuid: string;
        area: string;
      }
    >
  >({});
  const form = useFormContext();
  const buildingInformation = useMemo(() => form.getValues('pv_area'), [form.watch('pv_area')]);

  useEffect(() => {
    const location = form.getValues('location_information');

    if (location) {
      setTimeout(() => {
        mapInstanceRef.current.setView(
          {
            lat: location.latitude,
            lng: location.longitude,
          },
          20,
        );
      }, 0);
    }
  }, []);

  useEffect(() => {
    window.handlePvPolygonClick = async (id: number) => {
      // @ts-ignore
      id = String(id);

      const item = values[id];

      if (item.data) {
        item.data.floor_count_building = Math.floor(values[id].json.properties?.height / 2.5);
        item.data.building_height = values[id].json.properties?.height;
        item.data.area_pv = item.area;
      }

      // @ts-ignore
      item.layer._popup._closeButton.click();

      const modalData: Record<string, string> = await NiceModal.show(PvDetailsModal, {
        values: item.data ?? {},
        removePolygon: () => window.handlePvPolygonRemove(id),
      });

      if (modalData) {
        setValues(prevValue => ({
          ...prevValue,
          [id]: {
            ...item,
            data: modalData,
          },
        }));

        (item.layer as Layer).setPopupContent(createPopupContent(modalData.name_pv, String(id)));
      }
    };

    const valueToSet = Object.values(values).map((buildingData, index) => {
      return {
        area_pv: buildingData.area,
        ...buildingData.data,
        uuid: buildingData.uuid,
        polygon_data_pv: JSON.stringify(buildingData.json.geometry.coordinates[0]),
      };
    });

    console.log('valueToSet', Object.keys(valueToSet).length);
    if (Object.keys(valueToSet).length > 0) {
      form.setValue('pv_area', valueToSet);
    }
  }, [values]);

  useEffect(() => {
    window.handlePvPolygonRemove = async (id: number) => {
      setValues(prevValue => {
        const pvAreaId = prevValue[id]?.data?.id;
        const newValue = { ...prevValue };
        form.setValue(
          'pv_area',
          form.getValues('pv_area').filter((item: any) => item.uuid !== prevValue[id].data?.uuid),
        );
        mapInstanceRef.current?.removeLayer(newValue[id].layer);
        delete newValue[id];

        return newValue;
      });
    };
  }, []);

  const addPosition = useCallback(
    (
      id: string,
      layer: L.Layer,
      json: Feature<Polygon>,
      area: string,
      feature?: Record<string, any>,
      uuid?: string,
    ) => {
      setValues(v => {
        return {
          ...v,
          [id]: {
            layer,
            json,
            data: feature ?? { name_pv: `PV Fläche ${Object.keys(v).length + 1}` },
            uuid: uuid ?? feature?.uuid ?? uuidv7(),
            area: String(area),
          },
        };
      });
    },
    [],
  );

  return (
    <>
      {/* GRID STYLES ARE HARDCODED */}
      <div className="col-span-full buildings pv_area relative">
        <MapContainer center={[52.520008, 13.404954]} zoom={18}>
          <DrawControl
            drawnItemsRef={drawnItemsRef}
            mapInstanceRef={mapInstanceRef}
            addPosition={addPosition}
            // initialValue={buildingInformation}
          />
        </MapContainer>

        <MapOverlay
          isVisible={step !== null}
          onClick={() => {
            /* (optional) ignore clicks to pass through, or close */
          }}
        >
          {/* {step === 'select' && (
            <ToolModal
              title="Gebäudeauswahl"
              description="Sie können Ihr Gebäude auch durch Kicken auf die rot markierte Fläche auswählen."
              buttonText="Fertig"
              onClose={() => setStep(null)}
              onAction={() => setStep(null)}
              isVisible
            />
          )} */}
          {step === 'draw' && (
            <ToolModal
              title="PV-Flächen Markierung"
              description="Zeichnen Sie mögliche PV-Flächen durch setzen mehrerer Punkte in der Karte ein. Setzen Sie den letzten Punkt auf den Ersten um das Zeichnen zu beenden."
              buttonText="Nächste"
              onClose={() => setStep(null)}
              onAction={() => setStep(null)} // When "Next" is clicked, switch to 'select' mode
              isVisible
            />
          )}
        </MapOverlay>
      </div>
    </>
  );
};

export default PvAreaSelector;
